import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import {
  Typography,
  Card,
  CardContent,
  FormControl,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { GetApp } from '@mui/icons-material';

import { withStyles } from '@mui/styles';

import { withRouter, ProviderNotesUtil } from 'utils';
import Chart from 'react-apexcharts';
import { axiosInstance, StringUtil, ColorUtil } from 'utils';
import {
  CsvUtil,
  StoreUtil,
  DataUtil,
  EncounterTypeUtil,
} from 'doctivity-shared/utils';
import { LoadingView } from './LoadingView';

import { loadAnalytics } from 'store/actions/analyticsActions';
import { listActiveUsers } from 'store/actions/usersActions';
import fileDownload from 'js-file-download';
import { format, parse } from 'date-fns';
// Fixed reference so date-fns parse() fills unspecified fields deterministically.
const DATE_PARSE_REF = new Date(2000, 0, 1);

const styles = (theme) => ({
  filters: {
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  spacer: {
    flex: 1,
  },
  pdfButton: {
    flex: 1,
  },
  pdfIcon: {
    fontSize: 20,
    fontWeight: 200,
  },
  filterSpace: {
    width: 4,
  },
  rangeControl: {
    minWidth: 90,
  },
  userFilter: {
    minWidth: 270,
  },
  typeControl: {
    width: 180,
  },
});

class ProviderCommentsTaggedPerDayChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterUser: null,
      types: ProviderNotesUtil.allCountTypes,
    };
  }

  componentDidMount() {
    if (StoreUtil.needsLoadNoCache(this.props.activityNotesTaggedPerDay)) {
      this.fetchData();
    }

    if (
      StoreUtil.needsLoadLongCache(this.props.users) ||
      this.props.usersClientId !== this.props.app.selectedClient
    ) {
      this.fetchUsers();
    }
  }

  componentDidUpdate(prevProps) {
    // anytime the client changes
    if (this.props.app.selectedClient !== prevProps.app.selectedClient 
      || this.props.filterUser !== prevProps.filterUser
      || this.props.encounterType !== prevProps.encounterType
      || this.props.dateRange.startDate !== prevProps.dateRange.startDate
      || this.props.dateRange.endDate !== prevProps.dateRange.endDate) {
      this.fetchData();
    } else if (
      StoreUtil.needsLoadMediumCache(this.props.activityNotesTaggedPerDay)
    ) {
      this.fetchData();
    } else if (
      StoreUtil.hasSavedSinceUpdate(
        this.props.editComment,
        prevProps.editComment
      )
    ) {
      this.fetchData();
    }

    if (
      StoreUtil.needsLoadLongCache(this.props.users) ||
      this.props.usersClientId !== this.props.app.selectedClient
    ) {
      this.fetchUsers();
    }
  }

  getDateUnits() {
    const { startDate, endDate } = this.props.dateRange;

    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000 > 31
      ? 'months'
      : 'days';
  }

  fetchData() {
    const { dispatch, clientId, filterUser } = this.props;
    let offset = this.getOffset();

    let filter = {
      client_id: clientId,
      user_id: filterUser?.id,
      encounter_type_id: this.props.encounterType,
      comment_date: {
        $gte: this.props.dateRange.startDate.toISOString(),
        $lte: this.props.dateRange.endDate.toISOString(),
      },
    };

    if (this.props.encounterType === 'All') {
      filter = {
        client_id: clientId,
        user_id: filterUser?.id,
        comment_date: {
          $gte: this.props.dateRange.startDate.toISOString(),
          $lte: this.props.dateRange.endDate.toISOString(),
        },
      };      
      
    }

    dispatch(
      loadAnalytics({
        types: this.state.types,
        type: 'PROVIDER_COMMENTS_TAGGED_PER_DAY',
        filter: filter,
        dateUnits: this.getDateUnits(),
        offset: offset
      })
    );
  }

  getOffset() {
    const d = new Date();
    let diff = d.getTimezoneOffset()/60;

    if (diff> 0) {
      diff = '-' + diff.toString() + ':00';
    } else {
      diff = '+' + diff.toString() + ':00';
    }
    return diff;
  }

  fetchUsers() {
    const { dispatch, app } = this.props;

    dispatch(listActiveUsers(app.selectedClient));
  }

  fetchCSVDownload() {
    const { filterUser } = this.props;
    const { clientId } = this.props;
    let offset = this.getOffset();
    let dateUnits = this.getDateUnits();

    let filter;

    if (this.props.encounterType === 'All') {
      filter = {
          client_id: clientId,
          user_id: filterUser?.id,
          comment_date: {
            $gte: this.props.dateRange.startDate.toISOString(),
            $lte: this.props.dateRange.endDate.toISOString(),
            
          },
          user_id: filterUser?.id,
        }
    } else {
      filter =  {
          client_id: clientId,
          user_id: filterUser?.id,
          encounter_type_id: this.props.encounterType,
          comment_date: {
            $gte: this.props.dateRange.startDate.toISOString(),
            $lte: this.props.dateRange.endDate.toISOString(),
            
          },
          user_id: filterUser?.id,
        }
    }



    axiosInstance
      .post('/Analytics', {
        type: 'PROVIDER_COMMENTS_TAGGED_PER_DAY',
        filter: filter,
        offset: offset,
        dateUnits: dateUnits,
        format: 'csv',
        headers: {
          'Content-Type': 'text/csv',
        },
      },
      {
        params: {
          selected_client_id: this.props.clientId,
        }
      })
      .then((response) => {
        const filename =
          filterUser && filterUser !== 'All Users'
            ? `${filterUser.first_name}_${
                filterUser.last_name
              }_doctivity_notes_tagged_per_day_${format(new Date(), 'yy_MM_dd')}.csv`
            : `doctivity_notes_tagged_per_day_${format(new Date(), 'yy_MM_dd')}.csv`;
        try {
          const data = this.formatCSV(response.data);
          fileDownload(data, filename);
        } catch (err) {
          console.error(`Could not format csv for ${filename}`);
          console.error(err);
          fileDownload(response.data, filename);
        }
      });
  }

  formatCSV(data) {
    let csvColumns = CsvUtil.stringToMatrix(data);
    if (csvColumns.length > 0) {
      let encounterTypeIndex = -1;
      let encounterDateIndex = -1;
      let encounterDayIndex = -1;
      for (let index = 0; index < csvColumns[0].length; index++) {
        if (csvColumns[0][index] === 'encounter_type_id') {
          csvColumns[0][index] = 'encounter_type';
          encounterTypeIndex = index;
        } else if (csvColumns[0][index] === 'encounter_date') {
          encounterDateIndex = index;
        } else if (csvColumns[0][index] === 'encounter_date_day') {
          encounterDayIndex = index;
        }
      }

      if (
        encounterTypeIndex > -1 &&
        encounterDateIndex > -1 
        //&& encounterDayIndex > -1
      ) {

        for (let index = 1; index < csvColumns.length; index++) {


          if (csvColumns[index].length > 1) {
            let encounterTypeName = 'Unspecified';
            if (csvColumns[index][encounterTypeIndex] != '') {
              let encounterType = parseInt(
                csvColumns[index][encounterTypeIndex],
                10
              );
              encounterTypeName = EncounterTypeUtil.getName(encounterType);
            }

            csvColumns[index][encounterTypeIndex] = encounterTypeName;

            const encounterDate = csvColumns[index][encounterDateIndex];
            const encounterDay = csvColumns[index][encounterDayIndex];
            let formattedDate;
            
            if (encounterDay) {
              formattedDate = format(parse(encounterDate + encounterDay.padStart(2, '0'), 'yyyyMMdd', DATE_PARSE_REF), 'MM/dd/yyyy');
            } else {
              formattedDate = format(parse(encounterDate, 'yyyyMM', DATE_PARSE_REF), 'MM/yyyy');
            }
            csvColumns[index][encounterDateIndex] = formattedDate;
          }
        }

        csvColumns = DataUtil.removeMatrixColumn(csvColumns, encounterDayIndex);
      }
    
    }

    return CsvUtil.matrixToString(csvColumns);
  }

  render() {
    const { classes, activityNotesTaggedPerDay, users, series, categories } = this.props;
    const { types } = this.state;

    if (!StoreUtil.isLoaded(activityNotesTaggedPerDay) || !StoreUtil.isLoaded(users)) {
      return <LoadingView />;
    }

    // calculate the steps based on maximum value
    let maxValues = [];
    let maxVal = 0;

    if (series.length>0) {
      let arr = series[0];
      arr.data.map(() => {
        maxValues.push(0);  
      })
    

      series.map((arr) => {
        let counter = 0;
        arr.data.map((val) => {
          maxValues[counter] = maxValues[counter] + val;
          counter++;
        })
      })
    
      maxVal = Math.max(...maxValues);
    }

    


    const largeStepSize=Math.max(Math.round(maxVal/1000)*100,25)
    
    const stepSize = maxVal <=20 ? 1 : (maxVal <= 50 ? 5 : (maxVal <= 200 ? 10 : largeStepSize));

  

    return (
      <div>
        <Card>
          <CardContent>
            <div className={classes.header}>
              <Typography component='h2' variant='h6' color='primary'>
                Tagged Per Day
              </Typography>
              <div className={classes.pdfButton}>
                <IconButton
                  onClick={() => {
                    this.fetchCSVDownload();
                  }}
                  size='large'
                >
                  <GetApp className={classes.pdfIcon} />
                </IconButton>
              </div>
              <div className={classes.spacer} />
            </div>

            <div className={classes.filters}>
              <div className={classes.filterSpace} />
              <FormControl className={classes.typeControl}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={types.length > 1 ? 'All' : types[0]}
                  onChange={this.onChangeType}
                  label='Type'
                >
                  <MenuItem value='All'>All</MenuItem>
                  {ProviderNotesUtil.allCountTypes.map((type, index) => (
                    <MenuItem key={index} value={type}>
                      {ProviderNotesUtil.formatCountTypeForDisplay(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className={classes.filterSpace} />
            </div>

            <Chart
              type='bar'
              height={500}
              options={{
                chart: {
                  id: 'TaggedPerDay',
                  toolbar: {
                    show: false,
                  },
                  animations: {
                    enabled: false,
                  },
                  stacked: true,
                  zoom: {
                    enabled: false  // ADD THIS to disable zoom on scroll
                  },
                },
                yaxis: {
                  stepSize: stepSize,
                  labels: {
                    minWidth: 100,
                    formatter: function(val) {
                      return val.toFixed(0);
                    },
                  },
                },
                xaxis: {
                  categories,
                  labels: {
                    hideOverlappingLabels: true,
                    rotate: -45,
                    rotateAlways: true
                  },
                  tickPlacement: 'on',
                },
                tooltip: {
                  enabled: true,
                  shared: true,
                  intersect: false,
                  y: {
                    formatter: StringUtil.numberForChart,
                  },
                },
                dataLabels: {
                  enabled: true,
                  dropShadow: {
                    enabled: true,
                    left: 0,
                    top: 0,
                    opacity: 0.4,
                  },
                  formatter: StringUtil.numberForChart,
                  offsetY: 7,
                },
                colors: ColorUtil.getColors(7),
              }}
              series={series}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  onChangeUser = (event, newUser) => {
    this.setState(
      { filterUser: newUser !== 'All Users' ? newUser : null },
      this.fetchData
    );
  };

  onChangeType = (event) => {
    const newType = event.target.value;

    this.setState(
      {
        types: newType !== 'All' ? [newType] : ProviderNotesUtil.allCountTypes,
      },
      this.fetchData
    );
  };
}

ProviderCommentsTaggedPerDayChart.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  router: PropTypes.object.isRequired,
  app: PropTypes.object,
  user: PropTypes.object,
  users: PropTypes.object,
  usersClientId: PropTypes.number,
  editComment: PropTypes.object,
  activityNotesTaggedPerDay: PropTypes.object.isRequired,
  dateRange: PropTypes.object,
  series: PropTypes.object,
  categories: PropTypes.object,
  types: PropTypes.object,
  filterUser: PropTypes.object,
  encounterType: PropTypes.object

};

function mapStateToProps(state) {
  const { app, analytics, user, users, providerComments, types } = state;

  return {
    app,
    user,
    types,
    clientId: app.selectedClient,
    users: StoreUtil.get(users, StoreUtil.COMMON_LIST),
    usersClientId: users.listClientId,
    editComment: StoreUtil.get(providerComments, StoreUtil.COMMON_EDIT_ITEM),
    activityNotesTaggedPerDay: analytics.activityNotesTaggedPerDay || {},
    series: analytics.seriesActivityNotesTagged,
    categories: analytics.categoriesTagged,
  };
}

const styled = withStyles(styles)(ProviderCommentsTaggedPerDayChart);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ProviderCommentsTaggedPerDayChart };
